import React, { useState, useEffect, useRef } from 'react';
import { LoginPageProps } from '../../../types';
import PhoneInput from '../../../components/PhoneInput';
import MathCaptcha, { MathCaptchaRef } from '../../../components/MathCaptcha';
import TermsAndConditions from '../../../components/TermsAndConditions';

type DesktopLoginProps = LoginPageProps;

const DesktopLogin: React.FC<DesktopLoginProps> = ({
  formData,
  isLoading,
  error,
  onInputChange,
  onSMSLogin,
  onMTSCLogin,
  onFNSLogin,
  onToggleTerms,
  onToggleOtherMethods,
  onVerifySMSCode,
  onResetCaptcha,
}) => {
  const [showSmsCodePanel, setShowSmsCodePanel] = useState(false);
  const [smsCode, setSmsCode] = useState('');
  const [currentPhone, setCurrentPhone] = useState('');
  const smsCodeInputRef = useRef<HTMLInputElement>(null);
  const mathCaptchaRef = useRef<MathCaptchaRef>(null);

  // Автофокус на поле ввода SMS кода при открытии панели
  useEffect(() => {
    if (showSmsCodePanel && smsCodeInputRef.current) {
      smsCodeInputRef.current.focus();
    }
  }, [showSmsCodePanel]);

  const handleSMSLoginClick = async () => {
    if (!formData.phoneNumber || !formData.captchaAnswer || 
        !formData.acceptTerms || !formData.acceptPrivacy || 
        !formData.acceptPersonalData || !formData.acceptPlatformTerms) {
      return; // Валидация уже есть в onSMSLogin
    }
    
    // Вызываем основную логику SMS логина из LoginPage
    await onSMSLogin();
    
    // Если onSMSLogin успешен, показываем панель ввода кода
    setCurrentPhone(formData.phoneNumber);
    setShowSmsCodePanel(true);
  };

  const handleVerifySmsCode = async () => {
    if (!smsCode.trim()) return;
    
    try {
      // Вызываем верификацию SMS кода через LoginPage
      await onVerifySMSCode(smsCode);
      
      // Если верификация успешна, панель закроется автоматически
      setShowSmsCodePanel(false);
      setSmsCode('');
    } catch (error) {
      console.error('Error verifying SMS code:', error);
    }
  };

  return (
    <div className="min-h-screen bg-[#141826] flex">
      {/* Left Panel - Logo and Welcome */}
      <div className="w-1/2 bg-gradient-to-br from-[#1a1f2e] to-[#141826] flex flex-col justify-center items-center p-12">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-[#7661d7] rounded-full flex items-center justify-center mb-8">
            <span className="text-white text-4xl">A</span>
          </div>
          <h1 className="text-white text-5xl mb-4">Сделка и Точка</h1>
          <p className="text-slate-400 text-lg leading-relaxed mb-4">
            Сервис для безопасных расчётов между продавцом и покупателем.
          </p>
          <p className="text-slate-500 text-base leading-relaxed">
            Создайте сделку и отправьте ссылку на оплату покупателю. После оплаты вы и покупатель подтверждаете сделку — тогда деньги поступают на вашу карту. При необходимости возможен возврат покупателю.
          </p>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-1/2 bg-[#141826] flex items-center justify-center p-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h2 className="text-white text-3xl mb-2">Добро пожаловать!</h2>
            <p className="text-slate-400 text-base">
              Войдите, чтобы создавать сделки, отправлять ссылки на оплату и получать деньги на карту после подтверждения с обеих сторон.
            </p>
          </div>

          {/* Login Form */}
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 mb-6">
            {/* Phone Number Input */}
            <div className="mb-6">
              <label htmlFor="phone" className="block text-white text-sm mb-2">
                Номер телефона
              </label>
              <PhoneInput
                value={formData.phoneNumber}
                onChange={(value) => onInputChange('phoneNumber', value)}
                label=""
                required
                error={!!error}
                disabled={isLoading}
              />
            </div>

            {/* Captcha Input */}
            <div className="mb-6">
              <MathCaptcha
                ref={mathCaptchaRef}
                value={formData.captchaAnswer}
                onChange={(value) => onInputChange('captchaAnswer', value)}
                label="Решите пример"
                required
                error={!!error}
                disabled={isLoading}
              />
            </div>

            {/* Terms and Conditions */}
            <div className="mb-6">
              <TermsAndConditions
                terms={{
                  acceptTerms: formData.acceptTerms,
                  acceptPrivacy: formData.acceptPrivacy,
                  acceptPersonalData: formData.acceptPersonalData,
                  acceptPlatformTerms: formData.acceptPlatformTerms
                }}
                onTermsChange={onInputChange}
                disabled={isLoading}
              />
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500/30 rounded-2xl p-4 mb-6">
                <div className="text-red-400 text-sm">{error}</div>
              </div>
            )}

            {/* Login Methods */}
            <div className="space-y-4">
              {/* SMS Login Button */}
              <button
                onClick={handleSMSLoginClick}
                disabled={isLoading}
                className="w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 bg-white text-gray-800 hover:bg-white/90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span>Вход...</span>
                ) : (
                  <span>Войти по SMS</span>
                )}
              </button>

              {/* MTS ID Button 
              <button
                onClick={onMTSCLogin}
                disabled={isLoading}
                className="w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 bg-slate-700 text-white hover:bg-slate-600 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span>Вход...</span>
                ) : (
                  <span>Войти через MTS ID</span>
                )}
              </button>

              {/* FNS Button 
              <button
                onClick={onFNSLogin}
                disabled={isLoading}
                className="w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 bg-slate-700 text-white hover:bg-slate-600 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span>Вход...</span>
                ) : (
                  <span>Войти через ФНС</span>
                )}
              </button>*/}
            </div>
          </div>

          {/* Info Panel */}
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-6">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                  <span className="text-gray-800 text-lg">i</span>
                </div>
              </div>
              <div className="flex-1 text-white text-base leading-relaxed">
                Войдите по SMS — после этого вы сможете создавать сделки, копировать ссылки на оплату и управлять профилем и картой для вывода средств.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SMS Code Modal */}
      {showSmsCodePanel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowSmsCodePanel(false)}
          />
          
          {/* Modal */}
          <div className="relative bg-[#141826] rounded-3xl w-full max-w-md mx-4 max-h-[80vh] overflow-y-auto">
            {/* Header */}
            <div className="text-white text-left p-6 border-b border-white/10">
              <button
                onClick={() => setShowSmsCodePanel(false)}
                className="absolute right-6 top-6 w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center text-white hover:bg-slate-600 transition-colors"
              >
                ✕
              </button>
              <h3 className="text-2xl">Введите код из SMS</h3>
              <p className="text-slate-400 text-sm mt-2">
                Код отправлен на номер {currentPhone}
              </p>
            </div>
            
            {/* Body */}
            <div className="text-white p-6">
              <div className="mb-6">
                <label htmlFor="sms-code" className="block text-white text-sm mb-2">
                  Код из SMS
                </label>
                <input
                  ref={smsCodeInputRef}
                  type="text"
                  id="sms-code"
                  value={smsCode}
                  onChange={(e) => setSmsCode(e.target.value)}
                  placeholder="Введите код"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-slate-400 focus:outline-none focus:border-white/20"
                  maxLength={6}
                />
              </div>
              
              <button
                onClick={handleVerifySmsCode}
                disabled={!smsCode.trim()}
                className="w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 bg-white text-gray-800 hover:bg-white/90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>Подтвердить</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DesktopLogin;
